import type { Context, Env, MiddlewareHandler } from "hono";
import type { ContentfulStatusCode } from "hono/utils/http-status";

export type DemoVariables = {
  requestId: string;
  requestStartedAt: number;
};

export type DemoEnv<Bindings extends object> = Env & {
  Bindings: Bindings;
  Variables: DemoVariables;
};

const mutationMethods = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export const defaultContentSecurityPolicy = [
  "default-src 'self'",
  "base-uri 'none'",
  "frame-ancestors 'none'",
  "object-src 'none'",
  "form-action 'self'",
  "img-src 'self' data:",
  "font-src 'self'",
  "style-src 'self' 'unsafe-inline'",
  "script-src 'self'",
  "connect-src 'self'",
].join("; ");

const getRequestId = <Bindings extends object>(c: Context<DemoEnv<Bindings>>) =>
  c.get("requestId") || crypto.randomUUID();

export const jsonError = <Bindings extends object>(
  c: Context<DemoEnv<Bindings>>,
  status: ContentfulStatusCode,
  code: string,
  message: string,
  headers?: Record<string, string>,
) => {
  const requestId = getRequestId(c);
  c.header("x-request-id", requestId);

  if (headers) {
    for (const [name, value] of Object.entries(headers)) {
      c.header(name, value);
    }
  }

  return c.json(
    {
      ok: false as const,
      error: { code, message },
      requestId,
    },
    status,
  );
};

export const requestIds = <Bindings extends object>(): MiddlewareHandler<DemoEnv<Bindings>> => {
  return async (c, next) => {
    const requestId = crypto.randomUUID();
    c.set("requestId", requestId);
    c.set("requestStartedAt", performance.now());
    c.header("x-request-id", requestId);
    await next();
    c.header("x-request-id", requestId);
  };
};

export const structuredRequestLogging = <Bindings extends object>(): MiddlewareHandler<
  DemoEnv<Bindings>
> => {
  return async (c, next) => {
    try {
      await next();
    } finally {
      const status = c.res.status || 500;
      const level = status >= 500 ? "error" : status >= 400 ? "warn" : "info";
      const entry = {
        level,
        event: "request.complete",
        requestId: getRequestId(c),
        method: c.req.method,
        path: c.req.path,
        status,
        durationMs: Math.round((performance.now() - c.get("requestStartedAt")) * 100) / 100,
      };
      const serialized = JSON.stringify(entry);

      if (level === "error") {
        console.error(serialized);
      } else if (level === "warn") {
        console.warn(serialized);
      } else {
        console.log(serialized);
      }
    }
  };
};

export const securityHeaders = <Bindings extends object>(
  contentSecurityPolicy = defaultContentSecurityPolicy,
): MiddlewareHandler<DemoEnv<Bindings>> => {
  return async (c, next) => {
    c.header("content-security-policy", contentSecurityPolicy);
    c.header("cross-origin-opener-policy", "same-origin");
    c.header("cross-origin-resource-policy", "same-origin");
    c.header("permissions-policy", "camera=(), geolocation=(), microphone=()");
    c.header("referrer-policy", "strict-origin-when-cross-origin");
    c.header("x-content-type-options", "nosniff");
    c.header("x-frame-options", "DENY");

    if (new URL(c.req.url).protocol === "https:") {
      c.header("strict-transport-security", "max-age=31536000; includeSubDomains");
    }

    await next();
  };
};

export const sameOriginMutations = <Bindings extends object>(): MiddlewareHandler<
  DemoEnv<Bindings>
> => {
  return async (c, next) => {
    if (!mutationMethods.has(c.req.method.toUpperCase())) {
      await next();
      return;
    }

    const fetchSite = c.req.header("sec-fetch-site");
    if (fetchSite && fetchSite !== "same-origin" && fetchSite !== "none") {
      return jsonError(
        c,
        403,
        "forbidden_origin",
        "Mutation requests must come from the same origin.",
      );
    }

    const originHeader = c.req.header("origin");
    if (!originHeader) {
      return jsonError(c, 403, "missing_origin", "Mutation requests require an Origin header.");
    }

    let requestOrigin: string;
    try {
      requestOrigin = new URL(originHeader).origin;
    } catch {
      return jsonError(c, 403, "invalid_origin", "The Origin header is invalid.");
    }

    if (requestOrigin !== new URL(c.req.url).origin) {
      return jsonError(
        c,
        403,
        "forbidden_origin",
        "Mutation requests must come from the same origin.",
      );
    }

    await next();
  };
};
